import { withStyles, Box, Card, TextField, MaterialSymbol, TableProps, ButtonProps, AccountUser, Modal } from 'vopay';
import { useState, useEffect } from 'react';
import { Title, Button, SideDrawer, Table } from 'UI';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useDataFetchIndicator } from '@/hooks';
import userPostDeleteUser from '@/hooks/useApi/usePostDeleteAccountUser';
import useAccountUsers from '@/hooks/useApi/useAccountUsers';
import useResetPassword from '@/components/Users/useResetPassword';
import AddUserDialog from './AddUserDialog';
import AccountUserPermissionsDrawer from '@/components/Users/UserPermissions/AccountUserPermissionsDrawer';
import styles from './UserManagementDrawer.module.scss';

const cn = withStyles(styles);

type UserTableRow = {
  Username: string;
  DateAdded: string;
  ViewButtons: ButtonProps[];
};

type UserManagementDrawerProps = {
  accountToken: string;
  accountName: string;
  open: boolean;
  onClose: () => void;
};

export default function UserManagementDrawer({ accountToken, accountName, open, onClose }: UserManagementDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.UserManagement' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });

  const toast = useToast();
  const alertDialog = useAlertDialog();
  const { permissions } = usePermissions();
  const { users, fetchUsers } = useAccountUsers();
  const { userSettings } = useUserSettingsProvider();
  const { resetPassword } = useResetPassword();
  const [, setLoading] = useDataFetchIndicator();
  const [filteredUsers, setFilteredUsers] = useState<AccountUser[]>([]);
  const [, , , deleteUserAsync] = userPostDeleteUser({ bearer: accountToken });
  const [searchTerm, setSearchTerm] = useState('');
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | undefined>(undefined);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const loadUsers = async () => {
      setLoading(true);
      try {
        await fetchUsers({ Timezone: userSettings.timezone }, accountToken);
      } finally {
        setLoading(false);
      }
    };
    loadUsers();
  }, [open]);

  useEffect(() => {
    const filteredUsersToSet = users.filter(
      (user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredUsers(filteredUsersToSet);
  }, [searchTerm, users]);

  const handleResetPasswordDialog = async (userEmail: string) => {
    const result = await alertDialog.open(
      t('ResetPassword.title', { defaultValue: 'Send Password Reset Email?' }),
      t('ResetPassword.content', {
        defaultValue: "This will send a secure password reset link to the user's email address.",
      }),
      {
        confirmLabel: t('ResetPassword.sendButton', { defaultValue: 'Send Reset Email' }),
        abortLabel: tGlobal('cancel', { defaultValue: 'Cancel' }),
      },
    );

    if (result === 'confirm') {
      setLoading(true);
      try {
        await resetPassword({
          Username: userEmail,
        });
        toast.success(t('ResetPassword.successMessage', { defaultValue: 'The link was sent successfully.' }));
      } catch (err) {
        console.error(err);
        toast.neutral(t('ResetPassword.failMessage', { defaultValue: 'Unable to send the link.' }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemoveUserDialog = async (userEmail: string) => {
    const result = await alertDialog.caution(
      t('RemoveUser.title', { defaultValue: 'Are you sure you want to delete the user?' }),
      t('RemoveUser.content', {
        defaultValue: 'This action cannot be undone.',
      }),
      {
        abortLabel: t('yes', { defaultValue: 'No' }),
        confirmLabel: t('no', { defaultValue: 'Yes' }),
      },
    );

    if (result === 'confirm') {
      setLoading(true);
      try {
        await deleteUserAsync({
          EmailAddress: userEmail,
        });
        await fetchUsers({ Timezone: userSettings.timezone }, accountToken);
        toast.success(t('RemoveUser.successMessage', { defaultValue: 'The user was deleted successfully.' }));
      } catch (err) {
        console.error(err);
        toast.neutral(t('RemoveUser.failMessage', { defaultValue: 'Unable to delete the link.' }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddUserDialog = () => {
    setIsAddUserOpen(true);
  };

  const tableProps: TableProps<UserTableRow> = {
    id: 'users-list',
    version: '2',
    zebra: true,
    columns: [
      { id: 'Username', header: t('email', { defaultValue: 'Email' }), align: 'left', field: 'Username' },
      { id: 'DateAdded', header: t('dateAdded', { defaultValue: 'Date Added' }), align: 'left', field: 'DateAdded' },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box jc="fe" gap="sm">
            {data.ViewButtons.map((btn) => {
              return (
                <Button key={btn.id} size="small" onClick={btn.onClick}>
                  {btn.label}
                </Button>
              );
            })}
          </Box>
        ),
      },
    ],
    rows: filteredUsers.map((user) => {
      const buttons: ButtonProps[] = [];

      if (permissions.ManageUserPermissions) {
        buttons.push({
          label: t('permissions', { defaultValue: 'Permissions' }),
          id: `user-change-Permissions-${user.DateAdded}`,
          onClick: () => {
            setSelectedUserEmail(user.Username);
            setIsPermissionsDrawerOpen(true);
          },
        });
      }

      if (permissions.AddUsers) {
        buttons.push({
          label: t('resetPasswordButton', { defaultValue: 'Reset Password' }),
          id: `user-change-${user.DateAdded}`,
          onClick: () => {
            handleResetPasswordDialog(user.Username);
          },
        });
        buttons.push({
          label: tGlobal('remove'),
          id: `user-delete-${user.DateAdded}`,
          onClick: () => {
            handleRemoveUserDialog(user.Username);
          },
        });
      }

      return {
        id: user.Username,
        data: {
          Username: user.Username,
          DateAdded: user.DateAdded,
          ViewButtons: buttons,
        },
      };
    }),
  };

  return (
    <>
      <SideDrawer
        isOpen={open}
        onClose={() => {
          onClose();
        }}
        title={
          <Box flx="col" gapSize="4px">
            <Title level="3">{t('title', { defaultValue: 'User Management' })}</Title>
            <div className={cn('name')}>{accountName}</div>
          </Box>
        }
        style={{
          width: 'calc(100% - var(--navLeftWidthSmall, 80px))',
        }}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <Card className={cn('card')}>
          <Box flx="col" gapSize="16px">
            <Box jc="fe" gapSize="16px">
              <TextField
                key="UserSearchKeyword"
                name="UserSearchKeyword"
                placeholder="Search user"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                variant="outlineShadow"
                elementLeft={<MaterialSymbol className={cn('searchIcon')} size="24px" icon="search" />}
              />
              {permissions.AddUsers && (
                <Button size="small" onClick={handleAddUserDialog} colorScheme="primary">
                  {t('addUserButton', { defaultValue: 'Add User' })}
                </Button>
              )}
            </Box>
            <Table {...tableProps} />
          </Box>
        </Card>
        {/* Modal used directly — SideDrawer creates a stacking context that broke the dialog provider's z-index, making it unclickable */}
        <Modal isVisible={isAddUserOpen} enableBgClick={false}>
          <AddUserDialog
            onClose={() => setIsAddUserOpen(false)}
            onSuccess={async () => {
              await fetchUsers({ Timezone: userSettings.timezone }, accountToken);
            }}
            existingEmails={users.map((u) => u.Username)}
            accountToken={accountToken}
          />
        </Modal>
      </SideDrawer>
      <AccountUserPermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        emailAddress={selectedUserEmail}
        accountToken={accountToken}
      />
    </>
  );
}
